import React, { useState, useCallback } from 'react';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import { useHistory } from 'react-router-dom';

import { Formik, FormikValues } from 'formik';
import * as yup from 'yup';

import AppBar from 'components/AppBar';
import Button from 'components/Buttons/Button';
import MaterialInput from 'components/MaterialInput';
import ProgressSteps from 'components/ProgressSteps';
import { Desktop, Mobile } from 'components/Responsive';
import { WizardProps } from 'components/Wizard';
import useStoreTranslation from 'hooks/useStoreTranslation';

import {
  PaddingContainer,
  Container,
  BottomBackground,
  Header,
  GroupButton,
  TransitionContainer,
  StyledForm,
  FormContainer,
  BottomBackgroundDesktop,
} from '../../styles';

interface Props {
  wizard: WizardProps;
}

const GroupOnboarding = ({ wizard }: Props) => {
  const history = useHistory();
  const { t } = useStoreTranslation();
  const [isFocus, setIsFocus] = useState(() => {
    if (wizard.data.group) {
      return true;
    }
    return false;
  });

  const [initialValues] = useState(() => {
    if (wizard.data.group) {
      return {
        name: wizard.data.group.name,
      };
    }
    return {
      name: '',
    };
  });

  const validationSchema = yup.object().shape({
    name: yup.string().trim().required().min(3).max(100),
  });

  const handleSubmit = useCallback(
    (data: any) => {
      wizard.setData({
        ...wizard.data,
        group: {
          ...data,
        },
      });
      wizard.nextStep();
    },
    [wizard],
  );

  const setFocus = useCallback(() => {
    setIsFocus(true);
  }, []);

  return (
    <Container>
      <Formik
        initialStatus={{ rightCheckIfIsValid: true }}
        initialValues={initialValues}
        validationSchema={validationSchema}
        isInitialValid={(formik: any) => validationSchema.isValidSync(formik.initialValues)}
        onSubmit={handleSubmit}
      >
        {({ isValid, submitForm }: FormikValues) => (
          <>
            <nav>
              <AppBar goBack={() => history.goBack()}>{t('groupOnboardingForm.title')}</AppBar>
              <ProgressSteps size={wizard.size} currentStep={wizard.currentStep} />
            </nav>

            <FormContainer>
              <StyledForm>
                <PaddingContainer>
                  <Header>
                    <h3>{t('groupOnboardingForm.header')}</h3>
                  </Header>

                  <MaterialInput name="name" onFocus={setFocus} label={t('groupOnboardingForm.groupName')} />
                </PaddingContainer>
              </StyledForm>

              <TransitionContainer>
                <ReactCSSTransitionGroup
                  transitionName="fade"
                  transitionEnterTimeout={500}
                  transitionLeaveTimeout={300}
                >
                  {isFocus ? (
                    <GroupButton marginBottom>
                      <Button onClick={submitForm} disabled={!isValid} value={t('groupOnboardingForm.nextButton')} />
                    </GroupButton>
                  ) : (
                    <>
                      <Desktop>
                        <BottomBackgroundDesktop>
                          <BottomBackgroundDesktop.Description>
                            {t('groupOnboardingForm.bottomDescription')}
                          </BottomBackgroundDesktop.Description>
                          <BottomBackgroundDesktop.Image>
                            <img alt="onboarding-group" src="/images/onboarding_group.svg" />
                          </BottomBackgroundDesktop.Image>
                        </BottomBackgroundDesktop>
                      </Desktop>
                      <Mobile>
                        <BottomBackground key="bottomBackground">
                          <BottomBackground.Image>
                            <img alt="onboarding-group" src="/images/onboarding_group.svg" />
                          </BottomBackground.Image>
                          <BottomBackground.Description>
                            {t('groupOnboardingForm.bottomDescription')}
                          </BottomBackground.Description>
                        </BottomBackground>
                      </Mobile>
                    </>
                  )}
                </ReactCSSTransitionGroup>
              </TransitionContainer>
            </FormContainer>
          </>
        )}
      </Formik>
    </Container>
  );
};

GroupOnboarding.defaultProps = {
  wizard: {},
};

export default GroupOnboarding;
